import type { SupabaseClient } from "@supabase/supabase-js";
import type { Account } from "../save/store";
import { CloudError, type CloudApi } from "./sync";
import type { ChildRow, PurchaseRow, RankRow, SessionRow } from "./wire";

/*
  The only code that talks to Supabase. The client library is loaded on first
  use, so signed-out play never downloads it. Without the two environment
  variables, accounts are switched off and the game is local-only.
*/

const URL = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string | undefined;

export const cloudAvailable = !!(URL && KEY);

let clientPromise: Promise<SupabaseClient> | null = null;

function client(): Promise<SupabaseClient> {
  if (!cloudAvailable) return Promise.reject(new CloudError("network", "accounts are off"));
  clientPromise ??= import("@supabase/supabase-js").then(({ createClient }) =>
    createClient(URL!, KEY!, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: false,
        // The original name, kept so signed-in devices stay signed in.
        storageKey: "ninja-maths-auth",
      },
    }),
  );
  return clientPromise;
}

/** Map a Supabase or network error to try-again, refused-for-good, or sign-in-again. */
export function toCloudError(e: unknown): CloudError {
  if (e instanceof CloudError) return e;
  const err = (e ?? {}) as { code?: string; status?: number; message?: string; name?: string };
  const msg = err.message ?? String(e);
  if (err.status === 401 || err.code === "PGRST301" || err.code === "PGRST303")
    return new CloudError("auth", msg);
  if (err.code && /^(22|23|42)/.test(err.code)) return new CloudError("rejected", msg);
  return new CloudError("network", msg);
}

async function call<T>(p: PromiseLike<{ data: T; error: unknown }>): Promise<T> {
  let res: { data: T; error: unknown };
  try {
    res = await p;
  } catch (e) {
    throw toCloudError(e);
  }
  if (res.error) throw toCloudError(res.error);
  return res.data;
}

// ─── Signing in ─────────────────────────────────────────────────────────────

/** Email a 6-digit sign-in code. */
export async function sendCode(email: string): Promise<void> {
  const sb = await client();
  await call(sb.auth.signInWithOtp({ email, options: { shouldCreateUser: true } }));
}

/** Check the code; on success the session is kept on this device. */
export async function verifyCode(email: string, code: string): Promise<Account> {
  const sb = await client();
  const data = await call(sb.auth.verifyOtp({ email, token: code, type: "email" }));
  const user = data.user;
  if (!user) throw new CloudError("auth", "no user");
  const familyId: unknown = await call(sb.rpc("ensure_family"));
  return { userId: user.id, email: user.email ?? email, familyId: String(familyId) };
}

/** Whether this device still has a session for `account`. */
export async function stillSignedIn(account: Account): Promise<boolean> {
  const sb = await client();
  const { data } = await sb.auth.getSession();
  return data.session?.user.id === account.userId;
}

export async function signOut(): Promise<void> {
  const sb = await client();
  await sb.auth.signOut({ scope: "local" });
}

// ─── Sync ───────────────────────────────────────────────────────────────────

const SESSION_COLUMNS =
  "id,seq,child_id,kind,mode,rank_index,score,tried,seconds,duration_ms,passed,audio,seed,ops," +
  "trials_version,coins,enemy,fighter,won,ending,points,damage,hits,move_used,battles_version," +
  "items,items_used,payload,occurred_at,local_date,answers(n,type_id,fact_key,correct,ms,passed)";

const RECORD_ARG = { record_session: "s", record_rank_change: "r", record_purchase: "p" } as const;

export const supabaseApi: CloudApi = {
  async upsertChild(c) {
    const sb = await client();
    return call(
      sb.rpc("upsert_child", {
        p_id: c.id,
        p_name: c.name || "Ninja",
        p_pin: c.pin,
        p_settings: c.settings,
        p_updated_at: c.updatedAt,
      }),
    ) as Promise<ChildRow>;
  },
  async removeChild(id) {
    const sb = await client();
    await call(sb.rpc("remove_child", { p_id: id }));
  },
  async record(fn, body) {
    const sb = await client();
    return call(sb.rpc(fn, { [RECORD_ARG[fn]]: body })) as Promise<number>;
  },
  async listChildren() {
    const sb = await client();
    return call(
      sb.from("children").select("id,name,pin,settings,updated_at,deleted_at").order("created_at"),
    ) as Promise<ChildRow[]>;
  },
  async sessionsAfter(after, limit) {
    const sb = await client();
    return call(
      sb.from("sessions").select(SESSION_COLUMNS).gt("seq", after).order("seq").limit(limit),
    ) as unknown as Promise<SessionRow[]>;
  },
  async rankChangesAfter(after, limit) {
    const sb = await client();
    return call(
      sb
        .from("rank_changes")
        .select("id,seq,child_id,mode,rank_index,reason,ops,occurred_at,local_date")
        .gt("seq", after)
        .order("seq")
        .limit(limit),
    ) as Promise<RankRow[]>;
  },
  async purchasesAfter(after, limit) {
    const sb = await client();
    return call(
      sb
        .from("purchases")
        .select("id,seq,child_id,item,level,price,shop_version,occurred_at,local_date")
        .gt("seq", after)
        .order("seq")
        .limit(limit),
    ) as Promise<PurchaseRow[]>;
  },
};

// ─── Parents ────────────────────────────────────────────────────────────────

export interface Dashboard {
  skills: { type_id: string; tried: number; right: number; median_ms: number | null }[];
  weekly: { week: string; op: string; tried: number; right: number }[];
  daily: { day: string; trial_ms: number; practice_ms: number; battle_ms: number }[];
  battles: { fought: number; won: number; recruited: number };
  coins: { earned: number; spent: number };
  items: { item: string; level: number }[];
}

export async function childDashboard(childId: string): Promise<Dashboard> {
  const sb = await client();
  return call(sb.rpc("child_dashboard", { p_child: childId })) as Promise<Dashboard>;
}

/** Everything stored for the family, as one JSON-ready object. */
export async function exportFamily(): Promise<object> {
  const sb = await client();
  const [children, sessions, ranks, purchases] = await Promise.all([
    call(sb.from("children").select("*")),
    call(sb.from("sessions").select("*,answers(*)").order("seq")),
    call(sb.from("rank_changes").select("*").order("seq")),
    call(sb.from("purchases").select("*").order("seq")),
  ]);
  return {
    app: "nine-scrolls",
    exported: new Date().toISOString(),
    children,
    sessions,
    rank_changes: ranks,
    purchases,
  };
}

/** Delete the family, its children, all play data and the logins. Cannot be undone. */
export async function deleteFamily(): Promise<void> {
  const sb = await client();
  await call(sb.rpc("delete_my_family"));
  await sb.auth.signOut({ scope: "local" });
}
